import moment from 'moment';

import { luftGet } from '../shared/http';
import { loggedUserRequestErrorHandler } from '../shared/http/error';

// ================================
// ACTIONS
// ================================
const GET_SCHEDULE_LIST = 'luft/scheduling/GET_SCHEDULE_LIST';
const GET_SCHEDULE_LIST_SUCCESS = 'luft/scheduling/GET_SCHEDULE_LIST_SUCCESS';
const GET_SCHEDULE_LIST_FAILURE = 'luft/scheduling/GET_SCHEDULE_LIST_FAILURE';

const GET_SCHEDULE_NF_LIST = 'luft/scheduling/GET_SCHEDULE_NF_LIST';
const GET_SCHEDULE_NF_LIST_SUCCESS =
  'luft/scheduling/GET_SCHEDULE_NF_LIST_SUCCESS';
const GET_SCHEDULE_NF_LIST_FAILURE =
  'luft/scheduling/GET_SCHEDULE_NF_LIST_FAILURE';

const DELETE_SCHEDULE = 'luft/scheduling/DELETE_SCHEDULE';
const DELETE_SCHEDULE_SUCCESS = 'luft/scheduling/DELETE_SCHEDULE_SUCCESS';
const DELETE_SCHEDULE_FAILURE = 'luft/scheduling/DELETE_SCHEDULE_FAILURE';

// ================================
// INITIAL STATE
// ================================
const initialState = {
  scheduleList: {
    isFetching: false,
    error: '',
    requested: '',
    data: {
      totalItems: 0,
      scheduleList: [],
    },
  },
  activeScheduleNFList: {
    isFetching: false,
    error: '',
    requested: '',
    data: [],
  },
  deleteSchedule: {
    isFetching: false,
    error: '',
    requested: '',
  },
};

// ================================
// REDUCER
// ================================
export default function reducer(state = initialState, action) {
  switch (action.type) {
    // =============================
    // SCHEDULE LIST
    // =============================
    case GET_SCHEDULE_LIST: {
      return {
        ...state,
        scheduleList: {
          ...state.scheduleList,
          isFetching: true,
          requested: action.requested,
        },
      };
    }

    case GET_SCHEDULE_LIST_SUCCESS: {
      return {
        ...state,
        scheduleList: {
          ...state.scheduleList,
          isFetching: false,
          data: action.data,
        },
      };
    }

    case GET_SCHEDULE_LIST_FAILURE: {
      return {
        ...state,
        scheduleList: {
          ...state.scheduleList,
          isFetching: false,
          error: action.error,
        },
      };
    }

    // =============================
    // SCHEDULE NF LIST
    // =============================
    case GET_SCHEDULE_NF_LIST: {
      return {
        ...state,
        activeScheduleNFList: {
          ...state.activeScheduleNFList,
          isFetching: true,
          requested: action.requested,
          data: [],
          error: '',
        },
      };
    }

    case GET_SCHEDULE_NF_LIST_SUCCESS: {
      return {
        ...state,
        activeScheduleNFList: {
          ...state.activeScheduleNFList,
          isFetching: false,
          data: action.data,
        },
      };
    }

    case GET_SCHEDULE_NF_LIST_FAILURE: {
      return {
        ...state,
        activeScheduleNFList: {
          ...state.activeScheduleNFList,
          isFetching: false,
          error: action.error,
        },
      };
    }

    // =============================
    // DELETE SCHEDULE
    // =============================
    case DELETE_SCHEDULE: {
      return {
        ...state,
        deleteSchedule: {
          ...state.deleteSchedule,
          isFetching: true,
          requested: action.requested,
          error: '',
        },
      };
    }

    case DELETE_SCHEDULE_SUCCESS: {
      return {
        ...state,
        scheduleList: {
          ...state.scheduleList,
          data: {
            ...state.scheduleList.data,
            scheduleList: state.scheduleList.data.scheduleList.filter(
              schedule => schedule.id !== action.scheduleId,
            ),
          },
        },
        deleteSchedule: {
          ...state.deleteSchedule,
          isFetching: false,
        },
      };
    }

    case DELETE_SCHEDULE_FAILURE: {
      return {
        ...state,
        deleteSchedule: {
          ...state.deleteSchedule,
          isFetching: false,
          error: action.error,
        },
      };
    }

    default: {
      return state;
    }
  }
}

// ================================
// ACTION CREATORS
// ================================
function _getScheduleList(requested) {
  return {
    type: GET_SCHEDULE_LIST,
    requested,
  };
}

function _getScheduleListSuccess(data) {
  return {
    type: GET_SCHEDULE_LIST_SUCCESS,
    data,
  };
}

function _getScheduleListFailure(error) {
  return {
    type: GET_SCHEDULE_LIST_FAILURE,
    error,
  };
}

function _getScheduleNFList(requested, data) {
  return {
    type: GET_SCHEDULE_NF_LIST,
    requested,
    data,
  };
}

function _getScheduleNFListSuccess(data) {
  return {
    type: GET_SCHEDULE_NF_LIST_SUCCESS,
    data,
  };
}

function _getScheduleNFListFailure(error) {
  return {
    type: GET_SCHEDULE_NF_LIST_FAILURE,
    error,
  };
}

function _deleteSchedule(requested) {
  return {
    type: DELETE_SCHEDULE,
    requested,
  };
}

function _deleteScheduleSuccess(scheduleId) {
  return {
    type: DELETE_SCHEDULE_SUCCESS,
    scheduleId,
  };
}

function _deleteScheduleFailure(error) {
  return {
    type: DELETE_SCHEDULE_FAILURE,
    error,
  };
}

// ================================
// THUNKS
// ================================
export function getScheduleList(page = 1, amount = 10, query = '') {
  return function (dispatch) {
    const requested = moment().format();
    dispatch(_getScheduleList(requested));

    (async () => {
      const params = {
        page,
        amount,
        query,
      };

      try {
        const response = await luftGet('/Scheduling/GetScheduleList', params);
        dispatch(_getScheduleListSuccess(response.data));
      } catch (err) {
        loggedUserRequestErrorHandler(
          dispatch,
          err,
          _getScheduleListFailure(err),
        );
      }
    })();
  };
}

export function cancelSchedule(scheduleId) {
  return function (dispatch) {
    const requested = moment().format();
    dispatch(_deleteSchedule(requested));

    (async () => {
      try {
        await setTimeout(() => {
          dispatch(_deleteScheduleSuccess(scheduleId));
        }, 1000);
      } catch (error) {
        dispatch(_deleteScheduleFailure(error.message));

        throw new Error(error.message);
      }
    })();
  };
}

export function getScheduleNfList(scheduleId) {
  return function (dispatch) {
    const requested = moment().format();
    dispatch(_getScheduleNFList(requested));

    (async () => {
      const params = {
        scheduleId,
      };

      try {
        const response = await luftGet('/Scheduling/GetScheduleNFList', params);
        dispatch(_getScheduleNFListSuccess(response.data));
      } catch (err) {
        loggedUserRequestErrorHandler(
          dispatch,
          err,
          _getScheduleNFListFailure(err.message),
        );
      }
    })();
  };
}
